// The complete Ads API reference as a single llms-full.txt style Markdown
// document. Served to AI agents via GET /api/v1/docs.md (authenticated with an
// API key) and copied from the docs page's "Copy for AI" button, following the
// llms.txt convention for AI-readable documentation.

export const DOCS_MARKDOWN = `# Orange Trail Ads API

> Run an Orange Trail ad account entirely over HTTPS: create campaigns, target audiences, upload creatives, launch ads, track conversions (pixel + server-side CAPI), and read delivery and attribution. This is the complete reference, formatted for AI agents.

Base URL: https://orangetrail.app/api/v1
Authentication: every request sends the header \`Authorization: Bearer otk_live_...\` (an Orange Trail API key, created in the dashboard under Settings -> Developer).
Content type: \`Content-Type: application/json\` on every POST and PATCH.
Idempotency: optionally send \`Idempotency-Key: <unique string>\` on writes to make them safe to retry.
Versioning: none needed; Orange Trail pins the API version server side.
Scoping: every API key belongs to exactly one ad account. All requests act as that account. Never send an account identifier; any \`account_id\` you send is ignored and replaced. The ad account ID (format \`biz_...\`) appears in responses and reports.
Machine-readable copy of this document: GET /api/v1/docs.md (same authentication).

## Prerequisites

- Create an API key in the dashboard: Settings -> Developer. The secret (\`otk_live_...\`) is shown once. Up to 5 active keys per ad account.
- BILLING IS DASHBOARD-ONLY: before campaigns can launch or spend, the ad account's payment method must be set up in the dashboard under Settings -> Billing. There is no billing API. Launch and spend requests without active billing fail with 409.
- An advertising identity (Facebook Page) must be connected or created in the dashboard under Settings -> Identity; ads reference it as \`social_accounts\`.

## Object model

- Ad campaign (\`adcamp_...\`): owns the objective and optionally the budget.
- Ad group (\`adset_...\`): owns budget (by default), optimization, and targeting. Belongs to a campaign.
- Ad (\`ad_...\`): owns creative and copy. Belongs to an ad group.
- Lifecycle actions on all three levels: POST /{resource}/{id}/pause, POST /{resource}/{id}/unpause, POST /{resource}/{id}/duplicate.

## Quickstart: launch a campaign

1. Verify the key:
\`\`\`bash
curl https://orangetrail.app/api/v1/me -H "Authorization: Bearer otk_live_..."
# => { "ad_account_id": "biz_XXXX", "name": "...", "key": { ... } }
\`\`\`
2. Create a draft campaign (drafts never spend):
\`\`\`bash
curl -X POST https://orangetrail.app/api/v1/ad_campaigns \\
  -H "Authorization: Bearer otk_live_..." -H "Content-Type: application/json" \\
  -d '{ "title": "Summer sale", "objective": "sales", "platform": "meta" }'
# => { "id": "adcamp_XXXX", "status": "draft", ... }
\`\`\`
3. Create an ad group with budget and targeting:
\`\`\`bash
curl -X POST https://orangetrail.app/api/v1/ad_groups \\
  -H "Authorization: Bearer otk_live_..." -H "Content-Type: application/json" \\
  -d '{ "ad_campaign_id": "adcamp_XXXX", "budget_amount": 50, "budget_type": "daily", "optimization_goal": "conversions", "conversion_event": "purchase", "conversion_location": "website", "regions": { "include": { "countries": ["US"] } }, "placements": "automatic" }'
\`\`\`
4. Upload a creative, then PUT the binary to the returned URL and poll GET /files/{id} until processed:
\`\`\`bash
curl -X POST https://orangetrail.app/api/v1/files \\
  -H "Authorization: Bearer otk_live_..." -H "Content-Type: application/json" \\
  -d '{ "filename": "hero.jpg" }'
\`\`\`
5. Create the ad, then launch by setting the campaign's status to active:
\`\`\`bash
curl -X POST https://orangetrail.app/api/v1/ads \\
  -H "Authorization: Bearer otk_live_..." -H "Content-Type: application/json" \\
  -d '{ "ad_group_id": "adset_XXXX", "creatives": [{ "id": "file_XXXX" }], "primary_texts": ["Our biggest sale."], "headlines": ["Up to 50% off"], "call_to_action": "shop_now", "url": "https://yourstore.com/sale", "social_accounts": [{ "id": "sacc_XXXX" }] }'

curl -X PATCH https://orangetrail.app/api/v1/ad_campaigns/adcamp_XXXX \\
  -H "Authorization: Bearer otk_live_..." -H "Content-Type: application/json" \\
  -d '{ "status": "active" }'
\`\`\`

## Identity

- GET /me -> { ad_account_id, name, key: { id, name, prefix } }.

## Ad campaigns

Endpoints:
- GET /ad_campaigns (query: first, after, before, direction)
- GET /ad_campaigns/{id}
- POST /ad_campaigns
- PATCH /ad_campaigns/{id} (send { "status": "active" } to launch a draft)
- DELETE /ad_campaigns/{id}
- POST /ad_campaigns/{id}/pause | /unpause | /duplicate (duplicate body: { count?, preserve_engagement? })

Create/update fields:
- title (string, required): display name.
- objective (enum, required): awareness | traffic | engagement | leads | sales.
- platform (enum, required): meta.
- budget_optimization (enum): ad_campaign (one shared budget) | ad_group (default; each group budgets itself).
- budget_amount (number) + budget_type (enum: daily | lifetime): only when budget_optimization = ad_campaign.
- bid_type (enum): minimum_cost | average_target | maximum_target; targets use desired_cost_per_result (number).
- starts_at / ends_at (ISO 8601): delivery window for campaign-budget campaigns.
- special_ad_categories (enum[]): housing | employment | financial_products | politics.

Responses include per-object metrics: spend, impressions, clicks, reach, results, purchases, leads, return_on_ad_spend, cost_per_result, plus status, delivery_status, and open issues[].

## Ad groups

Endpoints:
- GET /ad_groups (query: ad_campaign_id, pagination)
- GET /ad_groups/{id}
- POST /ad_groups
- PATCH /ad_groups/{id}
- DELETE /ad_groups/{id}
- GET /ad_groups/targeting_options?platform=meta&search=... (search the taxonomy for detailed_targeting IDs; platform is required)
- POST /ad_groups/estimate_reach (estimate audience size; the body requires platform: meta)
- POST /ad_groups/{id}/pause | /unpause | /duplicate

Create/update fields:
- ad_campaign_id (string, required).
- budget_amount (number) + budget_type (daily | lifetime): omit when the campaign owns the budget.
- optimization_goal (enum): conversions | link_clicks | landing_page_views | reach | impressions | engagement | conversations | video_views | lead_generation | quality_lead | value | page_likes | event_responses | and more.
- conversion_event (string): purchase | lead | add_to_cart | complete_registration | schedule | contact | view_content | initiated_checkout | subscribe | start_trial | or any custom pixel event name.
- conversion_location (enum): website | profile | instagram_and_facebook | messaging | on_ad | instant_forms | instant_forms_and_messenger | website_and_instant_forms.
- demographics (object): { minimum_age, maximum_age, gender (all|male|female), automatic (boolean; platform-managed audience expansion) }.
- regions (object): { include, exclude }, each { countries: ["US", ...], cities: [{ key, name }], custom_locations: [{ latitude, longitude, radius }], country_groups (include only) }.
- detailed_targeting (object): { interests: [{ id, name }], behaviors: [{ id, name, behavior_type?, period? }], demographics: [{ id, type, name }] }. Max 100 per section; send complete intended state; not combinable with demographics.automatic or special_ad_categories.
- placements ("automatic" | object[]): per-platform positions. facebook: feed, story, marketplace, search, reels, instream_video, ...; instagram: stream, story, explore, reels, ...; messenger: story; audience_network; threads; whatsapp: status.
- audiences (object): { include: ["adaud_..."], exclude: ["adaud_..."] }.
- devices (object): { platforms: ["mobile"|"desktop"], operating_systems: [{ os: ios|android, minimum_version? }] }.
- languages (string[]): ISO 639 codes.
- dynamic_creative (boolean, creation only).
- frequency_cap (object, awareness campaigns): { maximum_impressions, per_days }.
- message_apps (enum[], messaging location only): messenger | instagram | whatsapp.
- starts_at / ends_at (ISO 8601).

## Ads

Endpoints:
- GET /ads (query: ad_group_id or ad_campaign_id, pagination)
- GET /ads/{id}
- POST /ads
- PATCH /ads/{id}
- DELETE /ads/{id}
- POST /ads/{id}/pause | /unpause | /duplicate

Create/update fields:
- ad_group_id (string) OR ad_group (inline ad group object, same shape as POST /ad_groups) to create both together.
- creatives (object[]): [{ id: "file_...", format? (square|vertical|horizontal), crop? ({ x, y, width, height }) }]. Two or more entries without formats become a carousel (2-10, in order).
- primary_texts / headlines / descriptions (string[]): copy variants.
- call_to_action (enum): shop_now | learn_more | sign_up | subscribe | contact_us | download | get_offer | get_quote | book_now | order_now | apply_now | watch_more | and more.
- url (string): destination. Its query string is split into url_parameters automatically.
- url_parameters (object): extra query params; attribution keys (utm_source, utm_medium, utm_content, wacid, wasid, waid, ...) are reserved and rejected.
- social_accounts (object[]): [{ id: "sacc_..." }] from GET /social_accounts (Facebook Page and optional Instagram profile).
- existing_post_id (string) + post_source (facebook | instagram): promote an existing post instead of creatives.
- lead_form (object) or lead_form_id (string): instant lead form when the group's conversion_location is an instant-forms destination.
- messaging_config (object): { message, keyword } welcome copy for click-to-message.
- title (string): display name.

## Files

- POST /files: { filename (required), byte_size?, multipart?, visibility? (public|private) } -> returns upload_url plus upload_headers. PUT the binary to upload_url sending every upload_headers entry verbatim.
- POST /files/{id}/complete: finalize a multipart upload with part ETags.
- GET /files/{id}: poll until processed before using in an ad.
- GET /files: list.
Use multipart: true above ~100MB (5MB parts, required past 5GB).

## Social accounts

- GET /social_accounts: the Facebook Pages / Instagram profiles connected to this ad account (ids prefixed sacc_). Connect identities in the dashboard: Settings -> Identity.

## Pixel & websites

The pixel is a browser snippet that measures page views, links visitors to purchases, and attributes conversions to ads with first-party data. Install on EVERY page of the funnel, inside <head>, with the scope set to the ad account ID:

\`\`\`html
<script>
!function(w,d,s,u,n,a,b){if(w[n])return;a=w[n]={q:[],t:+new Date,s:[],o:u,track:function(){a.q.push([+new Date].concat([].slice.call(arguments)))},setScope:function(){a.s=[].slice.call(arguments).filter(function(x){return typeof x==="string"});a.q.push([+new Date,"setScope"].concat(a.s))},scope:function(){var c=[].slice.call(arguments);return{track:function(){a.q.push([+new Date].concat([].slice.call(arguments)).concat([{__scope:c}]))}}}};b=d.createElement(s);b.async=1;b.src=u+"/s.js";d.getElementsByTagName(s)[0].parentNode.insertBefore(b,d.getElementsByTagName(s)[0])}(window,document,"script","https://t.whop.tw","whop");
whop.setScope("biz_XXXXXXXXXXXX");
whop.track("page");
</script>
\`\`\`

Installing on a funnel builder? For these platforms the standard snippet works as-is; paste it into the header/tracking-code slot, not a page text block, so it loads on every page:
- GoHighLevel: Sites -> Funnels (or Websites) -> open it -> Settings -> Tracking Code -> Header Tracking (funnel level covers every page).
- ClickFunnels: Funnel Settings -> Custom Code -> Head Code (or the Sites app's global Header Code for every funnel).
- Kajabi: Settings -> Site Details -> Page Scripts -> Header Page Scripts.
- WordPress / WooCommerce: no <head> access by default; install a header-script plugin (WPCode or Insert Headers and Footers) and paste into its Header field.
- No header slot anywhere: add the same snippet via Google Tag Manager as a Custom HTML tag firing on all pages.

Shopify, WebinarJam, Calendly, and iClosed need more than the standard snippet:

### Shopify

Shopify's checkout is sandboxed and ignores scripts pasted into theme.liquid, so a normal install never sees it. Use Shopify's Custom Pixel instead: Settings -> Customer events -> Add custom pixel, paste the script below (replace the account ID with yours), then Save AND Connect (Shopify sends nothing to an unconnected pixel). Keep the checkout_completed -> purchase mapping: a Shopify order is processed entirely inside Shopify, so nothing records it unless this script does. It is not a duplicate of automatic checkout tracking, which only covers platform checkouts.

\`\`\`javascript
const WHOP_ACCOUNT_ID = "biz_XXXXXXXXXXXX";

const WHOP_ENDPOINT = "https://t.whop.tw/conversions";

const TRACKED_EVENTS = new Set([
  "product_viewed", "collection_viewed", "search_submitted", "cart_viewed",
  "product_added_to_cart", "product_removed_from_cart", "checkout_started",
  "checkout_contact_info_submitted", "checkout_address_info_submitted",
  "checkout_shipping_info_submitted", "payment_info_submitted", "checkout_completed",
]);

const MAX_EVENT_NAME_LENGTH = 34;

const WHOP_STANDARD_EVENTS = new Set([
  "page", "lead", "contact", "schedule", "submit_application",
  "complete_registration", "view_content", "add_to_cart",
]);

// Standard names skip the character-limit problem entirely, since only custom
// names get prefixed with the account tag. Everything else falls through to a
// shopify_-prefixed custom name; the three checkout steps are shortened
// because Shopify's own names are too long once prefixed.
const EVENT_NAME_OVERRIDES = {
  page_viewed: "page",
  product_viewed: "view_content",
  product_added_to_cart: "add_to_cart",
  checkout_completed: "purchase",
  checkout_contact_info_submitted: "shopify_checkout_contact",
  checkout_address_info_submitted: "shopify_checkout_address",
  checkout_shipping_info_submitted: "shopify_checkout_shipping",
};

const TRACKED_QUERY_PARAMS = [
  "utm_id", "utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content",
  "fbclid", "gclid", "ttclid", "li_fat_id", "msclkid", "twclid", "rdt_cid",
  "sccid", "wbraid", "gbraid", "ig_sid",
];

function compact(object) {
  return Object.fromEntries(
    Object.entries(object).filter(
      ([, value]) => value !== null && value !== undefined && value !== "",
    ),
  );
}

async function readCookie(name) {
  try {
    return (await browser.cookie.get(name)) || null;
  } catch {
    return null;
  }
}

async function readCookies() {
  const [fbp, fbc, ga, ttp] = await Promise.all([
    readCookie("_fbp"), readCookie("_fbc"), readCookie("_ga"), readCookie("_ttp"),
  ]);
  return { fbp, fbc, ga, ttp };
}

function readQueryParameters(href) {
  if (!href) return {};
  try {
    const url = new URL(href);
    return compact(
      Object.fromEntries(
        TRACKED_QUERY_PARAMS.map((name) => [name, url.searchParams.get(name)]),
      ),
    );
  } catch {
    return {};
  }
}

function readTimezone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return null;
  }
}

function buildContext(event, cookies) {
  const documentContext = event.context?.document;
  const navigatorContext = event.context?.navigator;
  const screen = event.context?.window?.screen;
  const href = documentContext?.location?.href;

  return compact({
    user_agent: navigatorContext?.userAgent,
    language: navigatorContext?.language,
    timezone: readTimezone(),
    screen_resolution: screen ? \`\${screen.width}x\${screen.height}\` : null,
    fbp: cookies.fbp,
    fbc: cookies.fbc,
    ga: cookies.ga,
    ttp: cookies.ttp,
    ...readQueryParameters(href),
  });
}

function getAnonymousId(clientId) {
  if (!clientId) return null;
  const anonymousId = \`shopify_\${clientId}\`;
  if (anonymousId.length > 64) {
    console.error("Shopify client ID is too long");
    return null;
  }
  return anonymousId;
}

function getPixelEventName(shopifyEventName) {
  const name = EVENT_NAME_OVERRIDES[shopifyEventName] || \`shopify_\${shopifyEventName}\`;
  if (!WHOP_STANDARD_EVENTS.has(name) && name.length > MAX_EVENT_NAME_LENGTH) {
    console.error("Event name too long, Meta will drop it", name, name.length);
  }
  return name;
}

function getEventId(event, checkout) {
  if (event.name === "checkout_completed") {
    const orderOrCheckoutId = checkout?.order?.id || checkout?.token || event.id;
    return \`shopify:purchase:\${orderOrCheckoutId}\`;
  }
  return \`shopify:\${event.name}:\${event.id}\`;
}

function buildUser(event, checkout) {
  const anonymousId = getAnonymousId(event.clientId);
  if (!anonymousId) return null;

  const address = checkout?.shippingAddress || checkout?.billingAddress || {};
  const firstName = address.firstName || null;
  const lastName = address.lastName || null;

  return compact({
    anonymous_id: anonymousId,
    external_id: checkout?.order?.customer?.id || null,
    email: checkout?.email || null,
    phone: checkout?.phone || address.phone || null,
    first_name: firstName,
    last_name: lastName,
    name: [firstName, lastName].filter(Boolean).join(" ") || null,
    city: address.city || null,
    state: address.provinceCode || address.province || null,
    postal_code: address.zip || null,
    country: address.countryCode || address.country || null,
  });
}

async function sendPixelEvent(payload) {
  try {
    const response = await fetch(WHOP_ENDPOINT, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
      keepalive: true,
      credentials: "omit",
    });
    const responseBody = await response.text();
    if (!response.ok) {
      console.error("Pixel event failed", { status: response.status, response: responseBody, payload });
      return;
    }
    console.log("Pixel event sent", payload.event_name, responseBody);
  } catch (error) {
    console.error("Pixel request failed", payload.event_name, error);
  }
}

async function handleShopifyEvent(event) {
  const checkout = event.data?.checkout;
  const user = buildUser(event, checkout);
  if (!user) {
    console.warn("Shopify event missing client ID", event.name);
    return;
  }

  const cookies = await readCookies();
  const isPurchase = event.name === "checkout_completed";

  const payload = compact({
    account_id: WHOP_ACCOUNT_ID,
    event_name: getPixelEventName(event.name),
    event_id: getEventId(event, checkout),
    event_time: event.timestamp,
    action_source: event.name === "page_viewed" ? null : "website",
    url: event.context?.document?.location?.href || null,
    referrer_url: event.context?.document?.referrer || null,
    title: event.context?.document?.title || null,
    user,
    context: buildContext(event, cookies),
  });

  if (isPurchase && checkout?.totalPrice?.amount != null) {
    payload.value = Number(checkout.totalPrice.amount);
    const currency = checkout.totalPrice.currencyCode || checkout.currencyCode;
    if (currency) {
      payload.currency = currency.toLowerCase();
    }
  }

  await sendPixelEvent(payload);
}

analytics.subscribe("page_viewed", handleShopifyEvent);

analytics.subscribe("all_standard_events", async (event) => {
  if (event.name === "page_viewed" || !TRACKED_EVENTS.has(event.name)) {
    return;
  }
  await handleShopifyEvent(event);
});
\`\`\`

### WebinarJam

Integrations -> Integrate a 3rd Party Tracking System -> Embed Your Custom Tracking Code gives a tracking field per funnel step:
- Registration Form Tracking: the plain pixel snippet. The form is a popup with its own context; the visitor's email/name/phone fields live here, so this placement is what lets automatic field capture see them.
- Registration Page Tracking: the plain snippet PLUS the listener below, which fires lead the instant registration completes, before the thank-you redirect.
- Post-Registration Thank You Page: the plain snippet (backup page-view record). Live room / replay: plain snippet, optional.
- Registration hosted on your own site instead? Install the snippet on that page directly; WebinarJam's fields only cover its hosted pages.

\`\`\`javascript
window.addEventListener("message", function (e) {
  if (e.origin !== "https://event.webinarjam.com") return;

  let data;
  try {
    data = typeof e.data === "string" ? JSON.parse(e.data) : e.data;
  } catch {
    return;
  }

  if (data.func === "redirect" && typeof data.url === "string" && data.url.indexOf("/thank-you") !== -1) {
    whop.track("lead");
  }
});
\`\`\`

### Calendly / iClosed

Neither can tell your website when someone books, so: (1) install the standard snippet on the booking page (where the calendar is embedded or linked from); (2) build a thank-you page on your own site with the snippet too; (3) redirect bookers there: Calendly event type -> Edit -> More options -> Confirmation page -> Redirect to an external site, with "Pass event details to your redirected page" ON (that switch carries the booker's name and email); iClosed event settings -> Confirmation Page -> your thank-you URL; (4) under the snippet on the thank-you page, record the booking:

\`\`\`html
<script>
var params = new URLSearchParams(window.location.search);
whop.track("schedule", {
  email: params.get("invitee_email") || undefined,
  name: params.get("invitee_full_name") || undefined,
});
</script>
\`\`\`

Embedded Calendly (Share -> Add to Website) can skip the redirect: listen for message events where e.data.event === "calendly.event_scheduled" and fire whop.track("schedule"). That confirms the booking but carries no name/email; pass details collected earlier in the funnel, or use the redirect method.

Page views, checkout views, purchases, subscriptions, and trials are tracked automatically. Extra browser events (each optionally takes { value, currency }):
- Standard names: lead, schedule, submit_application, contact, complete_registration, view_content, add_to_cart.
- Custom names: whop.track("watched_vsl") or whop.track("quiz_completed", { value: 25, currency: "USD" }). Names under ~34 chars also forward to Meta as custom conversions.

Verification:
- POST /events/validate_pixel: { url? } reads a page for the snippet, or omit url to check from received events. Returns installed, host_events, page_events, last_fired_days.
- GET /events/pulse: live feed of the most recent incoming events.
Website analytics (visits by hostname, page, device_type, source) are served by the traffic metrics under GET /stats.

## Server-side events (CAPI)

POST /events sends conversions from your backend so off-page purchases still attribute to ads and improve optimization.

\`\`\`bash
curl -X POST https://orangetrail.app/api/v1/events \\
  -H "Authorization: Bearer otk_live_..." -H "Content-Type: application/json" \\
  -d '{ "event_name": "lead", "event_id": "lead_84921", "event_time": "2026-08-29T14:03:00Z", "value": 25, "currency": "usd", "url": "https://yourstore.com/thanks", "action_source": "website", "user": { "email": "jane@example.com", "phone": "+15551234567", "external_id": "cust_84921" }, "context": { "fbp": "fb.1....", "fbc": "fb.1....", "ip_address": "203.0.113.7", "user_agent": "Mozilla/5.0 ..." } }'
\`\`\`

Event fields: event_name (required; a standard pixel event: lead, schedule, submit_application, contact, complete_registration, view_content, add_to_cart; any other name, or custom + custom_name up to 35 chars, records as a custom event under that name; a purchase sent via CAPI is treated as an EXTERNAL purchase (a sale on your own checkout): it must carry a positive value or it is rejected, it counts toward purchase-optimized ad groups, and it is forwarded to Meta as a standard Purchase; checkouts on the ads platform itself are still tracked automatically and must not be re-sent), event_time (ISO 8601, default now), event_id (your dedup key: send the same id from browser and server and the event counts once), value, currency (ISO 4217), url, action_source (website | app | email | phone_call | chat | physical_store | system_generated | business_messaging | other).

user (match keys): email, phone, external_id, first_name, last_name, name, city, state, postal_code, country, birthdate (YYYY-MM-DD), gender (male|female), username, anonymous_id / linked_anonymous_id (the pixel's visitor ids, joins server events to browser sessions), user_id, member_id, membership_id.

context (attribution signals): fbp, fbc, fbclid (Meta), gclid, gbraid, wbraid (Google), ttclid, ttp (TikTok), msclkid, twclid, li_fat_id, sccid, rdt_cid, ip_address, user_agent, utm_source, utm_medium, utm_campaign, utm_content, utm_term, utm_id, ad_campaign_id, ad_set_id, ad_id, ga, language, timezone, screen_resolution, fingerprint.

Ingestion timing: a sent event is acknowledged immediately, is deduplicated by event_id, and becomes queryable by identifier within about 2 minutes.

Reading events back:
- GET /events?identifier=<email|phone|user id|cookie value>: a person's full journey.
- GET /events?from=...&to=...: recent account events (both ISO 8601; required without identifier).
- Filters: event (comma-separated names: payment.completed, pixel.lead, pixel.page, pixel.custom:<name>), source (whop:<campaign>:*, ext:meta:*, referrer:<domain>, direct), attribution_model (last_touch | first_touch), first/after/before/direction.

### Instant-form leads

Leads collected by Meta instant forms on your ads are recorded as lead events on the ad account (event_name pixel.lead). They are NOT a separate resource: read them from GET /events.

\`\`\`bash
curl "https://orangetrail.app/api/v1/events?event=pixel.lead&from=2026-09-01T00:00:00Z&to=2026-09-01T12:00:00Z" \\
  -H "Authorization: Bearer otk_live_..."
\`\`\`

Each lead event carries: event_id (Meta's submission id; stable across polls, use it to skip leads already processed), event_time (when the person submitted), user { email, phone, first_name, last_name, name, city, country } exactly as typed into the form, questions [{ question, answer, key, type, options }] for every form question including custom ones, and person_id (use GET /events?identifier=... for that person's full journey).

Timing: instant-form leads sync in from Meta every 15 minutes, so a lead appears within about 15 minutes of submission. To forward leads to a CRM, poll this endpoint every few minutes with from set to the previous check and push each new event_id. Leads also count in the leads metric on campaigns, ad groups, and ads.

## Reporting

- GET /stats: lists every metric with unit and breakdown properties. Ads metrics: ad_spend, ad_delivery. The traffic metric page_visits covers website visits (breakdowns: source, hostname, page, device_type).
- GET /stats/{metric}: from + to (required; dates or ISO timestamps), interval (minute|hour|day|week|month|year), breakdown_by (e.g. metric on ad_delivery for spend+impressions+clicks; source/hostname/page/device_type on traffic metrics), filters (hostname, page, device_type, source), time_zone (IANA, default UTC), convert_to (display currency, default usd).
- GET /ad_reports: daily delivery report rows.

## Conventions

- Pagination: lists return { data, page_info }; page with first + after (from page_info.end_cursor); some lists take before and direction.
- Errors: { error: { message, type, code? } }; Orange Trail auth errors return { message }.
- Status codes: 400 invalid parameters; 401 missing/revoked key; 404 unknown object, another account's object, or endpoint outside this API; 409 state conflict (including billing not set up); 429 rate limited (retry with backoff).
`;
